import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Sparkles, Send, Plus, Trash2, ThumbsUp, ThumbsDown, RotateCcw, Square,
  Building2, CalendarRange, Loader2, Database, ShieldCheck, AlertTriangle,
  Check, X, ExternalLink, PanelRight,
} from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const TOOL_LABELS = {
  get_company_context: "Contesto aziendale",
  get_reporting_periods: "Periodi di rendicontazione",
  get_current_reporting_period: "Periodo corrente",
  get_vsme_status: "Stato VSME",
  get_mef_status: "Questionario MEF - Banche",
  get_environmental_summary: "KPI Environmental",
  get_social_summary: "KPI Social",
  get_jobtimehr_sync_status: "Stato JOBTIMEHR",
  get_missing_esg_data: "Dati mancanti",
  get_data_to_verify: "Dati da verificare",
  explain_esg_metric: "Spiegazione metrica",
  get_framework_lineage: "Interoperabilità framework",
  get_governance_summary: "KPI Governance",
  search_esg_documents: "Ricerca documenti",
  get_document_evidence: "Evidenze documento",
  get_esg_pmi_score: "ESG PMI Score",
  explain_esg_score: "Spiegazione ESG Score",
  get_data_quality_score: "Data Quality Score",
  get_improvement_priorities: "Priorità di miglioramento",
  get_improvement_plan: "Piano di miglioramento",
  explain_improvement_action: "Spiegazione azione",
  get_simulation_summary: "Riepilogo simulazioni",
  simulate_esg_action: "Simulazione rapida",
  prepare_scenario_simulation: "Scenario di simulazione",
  get_report_readiness: "Prontezza report",
  get_report_summary: "Riepilogo report",
  explain_report_warning: "Avviso report",
  prepare_vsme_report: "Preparazione report",
  get_external_questionnaires: "Questionari esterni",
  get_external_questionnaire_status: "Stato questionario",
  get_questionnaire_missing_data: "Gap questionario",
  explain_external_question: "Spiegazione domanda",
  get_questionnaire_prefill_summary: "Prefill questionario",
  get_data_room_status: "Stato Data Room",
  get_data_room_contents: "Contenuti Data Room",
  get_data_room_access_log: "Accessi Data Room",
  prepare_data_room: "Preparazione Data Room",
  explain_share_warning: "Avviso condivisione",
  create_improvement_action: "Creazione azione piano",
  create_document_request: "Richiesta documento",
};

const RISK_STYLES = {
  HIGH: "bg-red-50 text-red-800 border-red-300",
  MEDIUM: "bg-amber-50 text-amber-800 border-amber-300",
};

function ContextHeader({ company, period, frameworks, mode }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 border-b border-slate-200 pb-3"
      data-testid="eva-context-header">
      <span className="inline-flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1">
        <Building2 className="w-3.5 h-3.5" aria-hidden="true" />
        {t("eva.contextCompany")}: <strong className="text-slate-800">{company?.denominazione}</strong>
      </span>
      {period && (
        <span className="inline-flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1">
          <CalendarRange className="w-3.5 h-3.5" aria-hidden="true" />
          Reporting Period: <strong className="text-slate-800">{period.year}</strong>
        </span>
      )}
      {(frameworks || []).map((f) => (
        <Badge key={f} variant="outline" className="bg-green-50 text-green-800 border-green-300">
          {f === "MEF_PMI_BANKS" ? "Questionario MEF - Banche" : f}
        </Badge>
      ))}
      {mode && (
        <Badge variant="outline"
          className={mode === "CONSULTANT"
            ? "bg-blue-50 text-blue-800 border-blue-300"
            : "bg-slate-50 text-slate-700 border-slate-300"}
          data-testid="eva-mode-badge">
          {mode === "CONSULTANT" ? t("eva.modeConsultant") : t("eva.modeClient")}
        </Badge>
      )}
    </div>
  );
}

function renderMarkdownLite(text) {
  // Rendering minimo: **grassetto** ed elenchi puntati. Il resto resta testo semplice.
  return text.split("\n").map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={`${i}-b${j}`}>{p.slice(2, -2)}</strong>
        : p
    );
    const isList = line.trim().startsWith("- ") || /^\d+\.\s/.test(line.trim());
    return (
      <span key={`${i}-${line.slice(0, 24)}`} className={isList ? "block pl-3" : "block"}>{parts}</span>
    );
  });
}

function ConfirmationCard({ conf, status, onResolve, resolving }) {
  const { t } = useI18n();
  const current = status || conf.execution_status || "PENDING";
  const params = conf.summary?.parameters || [];
  return (
    <div className="mt-3 rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4"
      data-testid={`eva-confirmation-card-${conf.confirmation_id || conf.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-amber-700" aria-hidden="true" />
        <p className="text-sm font-semibold text-slate-900">
          {t("eva.confirmation.proposes")}: {conf.summary?.tool_name || TOOL_LABELS[conf.tool_code] || conf.tool_code}
        </p>
        <Badge variant="outline"
          className={`${RISK_STYLES[conf.risk_level] || RISK_STYLES.MEDIUM} text-[10px] ml-auto`}>
          {conf.risk_level === "HIGH" ? t("eva.confirmation.highImpact") : t("eva.confirmation.write")}
        </Badge>
      </div>
      {params.length > 0 && (
        <ul className="text-xs text-slate-700 space-y-0.5 mb-3">
          {params.map((p) => (
            <li key={p.key}><span className="font-medium">{p.key}:</span> {p.value}</li>
          ))}
        </ul>
      )}
      {current === "PENDING" ? (
        <div className="flex items-center gap-2">
          <Button size="sm" className="bg-green-700 hover:bg-green-800 h-8"
            disabled={resolving}
            onClick={() => onResolve(conf.confirmation_id || conf.id, true)}
            data-testid={`eva-confirm-yes-${conf.confirmation_id || conf.id}`}>
            <Check className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {t("button.confirm")}
          </Button>
          <Button size="sm" variant="outline" className="h-8"
            disabled={resolving}
            onClick={() => onResolve(conf.confirmation_id || conf.id, false)}
            data-testid={`eva-confirm-no-${conf.confirmation_id || conf.id}`}>
            <X className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {t("button.cancel")}
          </Button>
          <span className="text-[10px] text-slate-500 ml-1">{t("eva.confirmation.hint")}</span>
        </div>
      ) : (
        <p className="text-xs font-medium text-slate-600"
          data-testid={`eva-confirmation-status-${conf.confirmation_id || conf.id}`}>
          {current === "EXECUTED" && t("eva.confirmation.executed")}
          {current === "CANCELLED" && t("eva.confirmation.cancelled")}
          {current === "EXPIRED" && t("eva.confirmation.expired")}
          {current === "FAILED" && t("eva.confirmation.failed")}
        </p>
      )}
    </div>
  );
}

function MessageBubble({ msg, onFeedback, feedbackGiven, confirmationStatus, onResolve, resolving }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`eva-message-${msg.role}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser ? "bg-green-700 text-white whitespace-pre-wrap" : "bg-white border border-slate-200 text-slate-800"}`}>
        {isUser ? msg.content : renderMarkdownLite(msg.content)}
        {!isUser && msg.streaming && (
          <span className="inline-block w-2 h-4 ml-1 bg-green-700 animate-pulse" aria-hidden="true" />
        )}
        {!isUser && (msg.confirmations || []).map((c) => (
          <ConfirmationCard key={c.confirmation_id} conf={c}
            status={confirmationStatus[c.confirmation_id]}
            onResolve={onResolve} resolving={resolving} />
        ))}
        {!isUser && !msg.streaming && (
          <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap items-center gap-2">
            {msg.tool_calls?.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-500"
                data-testid="eva-sources">
                <Database className="w-3 h-3" aria-hidden="true" />
                Dati utilizzati: {msg.tool_calls.map((t) => TOOL_LABELS[t.tool] || t.tool).join(", ")}
              </span>
            )}
            {msg.suggested_actions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 w-full mt-1">
                {msg.suggested_actions.map((a, i) => (
                  <Link key={a.route} to={a.route}
                    className="text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1 hover:bg-green-100 transition-colors"
                    data-testid={`eva-action-${i}`}>
                    {a.label} →
                  </Link>
                ))}
              </div>
            )}
            <div className="flex gap-1 ml-auto">
              <button type="button" aria-label="Risposta utile"
                className={`p-1 rounded hover:bg-slate-100 ${feedbackGiven === 1 ? "text-green-700" : "text-slate-400"}`}
                onClick={() => onFeedback(msg.id, 1)} data-testid={`eva-feedback-up-${msg.id}`}>
                <ThumbsUp className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
              <button type="button" aria-label="Risposta non utile"
                className={`p-1 rounded hover:bg-slate-100 ${feedbackGiven === -1 ? "text-red-600" : "text-slate-400"}`}
                onClick={() => onFeedback(msg.id, -1)} data-testid={`eva-feedback-down-${msg.id}`}>
                <ThumbsDown className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SourcesPanel({ boot, sources, pendingCount }) {
  const { t } = useI18n();
  return (
    <aside className="hidden lg:flex w-72 shrink-0 flex-col gap-4" data-testid="eva-context-panel">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
          <PanelRight className="w-3.5 h-3.5" aria-hidden="true" /> {t("eva.contextTitle")}
        </p>
        <dl className="text-xs text-slate-600 space-y-1.5">
          <div className="flex justify-between gap-2">
            <dt>{t("eva.contextCompany")}</dt>
            <dd className="font-medium text-slate-800 text-right">{boot.company?.denominazione}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt>{t("eva.contextPeriod")}</dt>
            <dd className="font-medium text-slate-800">{boot.period ? boot.period.year : "—"}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt>{t("eva.contextMode")}</dt>
            <dd className="font-medium text-slate-800">
              {boot.mode === "CONSULTANT" ? t("eva.modeConsultant") : t("eva.modeClient")}
            </dd>
          </div>
        </dl>
        {pendingCount > 0 && (
          <p className="mt-3 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5"
            data-testid="eva-pending-confirmations-note">
            <AlertTriangle className="w-3 h-3 inline mr-1" aria-hidden="true" />
            {t("eva.pendingConfirmations", { count: pendingCount })}
          </p>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex-1 min-h-0 overflow-y-auto">
        <p className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1.5">
          <Database className="w-3.5 h-3.5" aria-hidden="true" /> {t("eva.sourcesTitle")}
        </p>
        {sources.length === 0 ? (
          <p className="text-[11px] text-slate-500">
            {t("eva.sourcesEmpty")}
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="eva-sources-list">
            {sources.map((s, i) => (
              <li key={`${s.tool}-${i}`}>
                {s.citation?.route ? (
                  <Link to={s.citation.route}
                    className="flex items-center gap-1.5 text-xs text-green-800 hover:text-green-900 hover:underline"
                    data-testid={`eva-source-link-${i}`}>
                    <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
                    <span className="truncate">{s.citation.label}</span>
                  </Link>
                ) : (
                  <span className="text-xs text-slate-600">{TOOL_LABELS[s.tool] || s.tool}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

export default function Eva() {
  const { companyId, period } = useTenant();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  // Contesto pagina (punto 23): la route di provenienza arriva dalla CTA globale
  // e viene inviata al backend, che la valida e la inietta nel tool di help.
  const [pageRoute] = useState(() => searchParams.get("from") || "/eva");
  const [boot, setBoot] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(searchParams.get("q") || "");
  const [busy, setBusy] = useState(false);
  const [activeTools, setActiveTools] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [confirmationStatus, setConfirmationStatus] = useState({});
  const [resolving, setResolving] = useState(false);
  const abortRef = useRef(null);
  const bottomRef = useRef(null);
  const autoSentRef = useRef(false);

  const loadBoot = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/eva/bootstrap`,
        { params: period?.id ? { period_id: period.id } : {} });
      setBoot(data);
      setBootError(null);
    } catch (e) {
      if (e?.response?.status === 403) setBootError(formatApiError(e));
      else toast.error("Errore", { description: formatApiError(e) });
    }
  }, [companyId, period?.id]);

  useEffect(() => { loadBoot(); }, [loadBoot]);

  const qParam = searchParams.get("q");
  useEffect(() => {
    if (!autoSentRef.current && boot && qParam) {
      autoSentRef.current = true;
      setSearchParams({}, { replace: true });
      send(qParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boot, qParam]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeTools]);

  const openConversation = async (id) => {
    try {
      const { data } = await api.get(`/companies/${companyId}/eva/conversations/${id}`);
      setConvId(id);
      setMessages(data.messages || []);
      try {
        const { data: confData } = await api.get(
          `/companies/${companyId}/eva/conversations/${id}/confirmations`);
        const map = {};
        (confData.confirmations || []).forEach((c) => { map[c.id] = c.execution_status; });
        setConfirmationStatus(map);
      } catch { /* conferme non critiche */ }
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const newConversation = () => {
    setConvId(null);
    setMessages([]);
    setActiveTools([]);
    setConfirmationStatus({});
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/companies/${companyId}/eva/conversations/${id}`);
      if (convId === id) newConversation();
      await loadBoot();
      toast.success("Conversazione eliminata");
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const sendFeedback = async (messageId, rating) => {
    try {
      await api.post(`/companies/${companyId}/eva/messages/${messageId}/feedback`, { rating });
      setFeedback((f) => ({ ...f, [messageId]: rating }));
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const resolveConfirmation = async (confirmationId, confirmed) => {
    setResolving(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/eva/confirmations/${confirmationId}/resolve`,
        { confirmed });
      setConfirmationStatus((s) => ({ ...s, [confirmationId]: data.execution_status }));
      if (data.execution_status === "EXECUTED") {
        const msg = data.result?.message || "Azione eseguita correttamente.";
        setMessages((m) => [...m, {
          id: `conf-${confirmationId}`, role: "assistant",
          content: `✓ ${msg}`, tool_calls: [], confirmations: [],
        }]);
        toast.success("Azione eseguita", { description: msg });
      } else if (data.execution_status === "CANCELLED") {
        toast.info("Azione annullata");
      }
    } catch (e) {
      toast.error("Conferma non riuscita", { description: formatApiError(e) });
    } finally {
      setResolving(false);
    }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy || !companyId) return;
    setInput("");
    setBusy(true);
    setActiveTools([]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      let cid = convId;
      if (!cid) {
        const { data: conv } = await api.post(`/companies/${companyId}/eva/conversations`, null,
          { params: period?.id ? { period_id: period.id } : {} });
        cid = conv.id;
        setConvId(cid);
      }
      setMessages((m) => [...m,
        { id: `u-${Date.now()}`, role: "user", content },
        { id: "streaming", role: "assistant", content: "", streaming: true, confirmations: [] }]);
      const base = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(
        `${base}/api/companies/${companyId}/eva/conversations/${cid}/messages`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content, page_route: pageRoute }),
          signal: controller.signal });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Errore nella risposta di Eva");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalMeta = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const evLine = part.split("\n").find((l) => l.startsWith("event: "));
          const dataLine = part.split("\n").find((l) => l.startsWith("data: "));
          if (!evLine || !dataLine) continue;
          const evName = evLine.slice(7).trim();
          let payload = dataLine.slice(6);
          try { payload = JSON.parse(payload); } catch { /* testo grezzo */ }
          if (evName === "delta") {
            setMessages((m) => m.map((x) => x.id === "streaming"
              ? { ...x, content: x.content + payload } : x));
          } else if (evName === "tool") {
            setActiveTools((t) => payload.phase === "start"
              ? [...t, payload.name]
              : t.filter((n) => n !== payload.name));
          } else if (evName === "confirmation") {
            setMessages((m) => m.map((x) => x.id === "streaming"
              ? { ...x, confirmations: [...(x.confirmations || []), payload] } : x));
            setConfirmationStatus((s) => ({ ...s, [payload.confirmation_id]: "PENDING" }));
          } else if (evName === "done") {
            finalMeta = payload;
          } else if (evName === "error") {
            throw new Error(typeof payload === "string" ? payload : "Errore di Eva");
          }
        }
      }
      const meta = finalMeta || {};
      setMessages((m) => m.map((x) => x.id === "streaming"
        ? { ...x, id: meta.message_id || `a-${Date.now()}`, streaming: false,
            suggested_actions: meta.suggested_actions || [],
            tool_calls: (meta.sources || []).map((s) => ({
              tool: s.tool, ok: s.ok, citation: s.citation })) }
        : x));
      await loadBoot();
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error("Eva non risponde", { description: e.message });
      }
      setMessages((m) => m.filter((x) => x.id !== "streaming"));
    } finally {
      setBusy(false);
      setActiveTools([]);
      abortRef.current = null;
    }
  };

  const regenerate = () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (lastUser && !busy) {
      setMessages((m) => m.slice(0, m.findIndex((x) => x.id === lastUser.id)));
      send(lastUser.content);
    }
  };

  const sources = [];
  const seenTools = new Set();
  for (const m of messages) {
    for (const t of m.tool_calls || []) {
      if (t.ok && !seenTools.has(t.tool)) {
        seenTools.add(t.tool);
        sources.push(t);
      }
    }
  }
  const pendingCount = Object.values(confirmationStatus).filter((s) => s === "PENDING").length;

  if (!boot) {
    if (bootError) {
      return (
        <div className="max-w-lg mx-auto mt-16 rounded-lg border border-slate-200 bg-white p-6 text-center"
          data-testid="eva-access-denied">
          <ShieldCheck className="w-8 h-8 text-green-700 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-slate-900 mb-2">{t("eva.accessDeniedTitle")}</h1>
          <p className="text-sm text-slate-600">{t("eva.accessDeniedText")}</p>
        </div>
      );
    }
    return <p className="text-sm text-slate-600" data-testid="eva-loading">{t("eva.loading")}</p>;
  }

  return (
    <div className="flex gap-6 h-[calc(100vh-8rem)]" data-testid="eva-page">
      <aside className="hidden md:flex w-64 shrink-0 flex-col rounded-lg border border-slate-200 bg-white"
        data-testid="eva-conversations-sidebar">
        <div className="p-3 border-b border-slate-200">
          <Button className="w-full bg-green-700 hover:bg-green-800" size="sm"
            onClick={newConversation} data-testid="eva-new-conversation">
            <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> {t("eva.newConversation")}
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {(boot.conversations || []).map((c) => (
            <div key={c.id}
              className={`group flex items-center gap-1 rounded-md px-2 py-2 text-xs cursor-pointer transition-colors ${
                convId === c.id ? "bg-green-50 text-green-900 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}
              onClick={() => openConversation(c.id)}
              data-testid={`eva-conversation-${c.id}`}
              role="button" tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && openConversation(c.id)}>
              <span className="flex-1 truncate">{c.title}</span>
              <button type="button" aria-label="Elimina conversazione"
                className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600"
                onClick={(e) => deleteConversation(c.id, e)}
                data-testid={`eva-delete-conversation-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </div>
          ))}
          {(boot.conversations || []).length === 0 && (
            <p className="text-[11px] text-slate-500 px-2 py-3">{t("eva.noConversations")}</p>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col rounded-lg border border-slate-200 bg-slate-50 min-w-0">
        <div className="p-4 bg-white rounded-t-lg">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-md bg-green-700 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" aria-hidden="true" />
            </span>
            <div>
              <h1 className="text-base font-bold text-slate-900">{t("eva.title")}</h1>
              <p className="text-[11px] text-slate-500">{t("eva.subtitle")}</p>
            </div>
          </div>
          <ContextHeader company={boot.company} period={boot.period}
            frameworks={boot.frameworks} mode={boot.mode} />
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="eva-chat-area">
          {messages.length === 0 && (
            <div className="max-w-xl mx-auto mt-8" data-testid="eva-welcome">
              <div className="bg-white border border-slate-200 rounded-2xl p-5">
                <p className="text-sm font-semibold text-slate-900 mb-1">{t("eva.welcomeTitle")}</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t("eva.welcomeText")}
                  {boot.mode === "CONSULTANT" && (
                    <> {t("eva.welcomeConsultant")}</>
                  )}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(boot.suggestions || []).map((s) => (
                    <button key={s} type="button"
                      className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-full px-3 py-1.5 hover:bg-green-100 transition-colors"
                      onClick={() => send(s)}
                      data-testid={`eva-suggestion-${s.slice(0, 20).replace(/\W/g, "-")}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.id} msg={m} onFeedback={sendFeedback}
              feedbackGiven={feedback[m.id]}
              confirmationStatus={confirmationStatus}
              onResolve={resolveConfirmation} resolving={resolving} />
          ))}
          {activeTools.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="eva-tool-indicator">
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
              {t("eva.consulting")}: {activeTools.map((tl) => TOOL_LABELS[tl] || tl).join(", ")}…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="p-4 bg-white border-t border-slate-200 rounded-b-lg">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder={t("eva.placeholder")}
              rows={2}
              className="flex-1 resize-none"
              data-testid="eva-input"
              aria-label={t("eva.sendMessage")}
            />
            <div className="flex flex-col gap-1.5">
              {busy ? (
                <Button variant="outline" size="icon" onClick={() => abortRef.current?.abort()}
                  data-testid="eva-cancel" aria-label={t("eva.stopAnswer")}>
                  <Square className="w-4 h-4" aria-hidden="true" />
                </Button>
              ) : (
                <Button className="bg-green-700 hover:bg-green-800" size="icon"
                  onClick={() => send()} disabled={!input.trim()}
                  data-testid="eva-send" aria-label={t("eva.sendMessage")}>
                  <Send className="w-4 h-4" aria-hidden="true" />
                </Button>
              )}
              {messages.some((m) => m.role === "assistant" && !m.streaming) && !busy && (
                <Button variant="outline" size="icon" onClick={regenerate}
                  data-testid="eva-regenerate" aria-label={t("eva.regenerate")}>
                  <RotateCcw className="w-4 h-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            {t("eva.footerNote")}
          </p>
        </div>
      </div>

      <SourcesPanel boot={boot} sources={sources} pendingCount={pendingCount} />
    </div>
  );
}
